import React, { FC } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import { useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { object, string } from 'yup';
import TextField from '@material-ui/core/TextField';
import IconButton from '@material-ui/core/IconButton';
import DoneOutline from '@material-ui/icons/DoneOutline';
import ClearOutlined from '@material-ui/icons/ClearOutlined';
import CircularProgress from '@material-ui/core/CircularProgress';
import { updateDocument, getDocumentLoaders } from '../documents';

interface RenameFormProps {
  closeEdit: () => void;
  id: string | null;
  name: string;
  open?: boolean;
}

const RenameSchema = object().shape({
  name: string().required('components.name is required')
});

export type RenameSchemaProps = {
  name: string;
};

const RenameForm: FC<RenameFormProps> = ({ closeEdit, name, id }) => {
  const dispatch = useDispatch();
  const { isSavingItems } = useSelector(getDocumentLoaders);
  const { handleSubmit, control, errors } = useForm<RenameSchemaProps>({
    mode: 'onBlur',
    resolver: yupResolver(RenameSchema),
    defaultValues: {
      name
    }
  });
  const { t } = useTranslation();

  const onSubmit = async (formData: RenameSchemaProps) => {
    if (!id) {
      closeEdit();
      return;
    }
    await dispatch(updateDocument({ id, name: formData.name }));
    closeEdit();
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate style={{ display: 'flex', justifyContent: 'space-between' }}>
      <Controller
        as={
          <TextField
            autoComplete="new-password"
            helperText={t(get(errors, 'name.message'))}
            error={!isEmpty(get(errors, `name`))}
            label={t('components.name')}
            style={{ minWidth: '400px' }}
            variant="outlined"
            fullWidth
          />
        }
        control={control}
        defaultValue=""
        name="name"
      />
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <IconButton aria-label="menu" color="secondary" disabled={get(isSavingItems, 'id')} onClick={() => closeEdit()}>
          <ClearOutlined />
        </IconButton>
        <IconButton aria-label="menu" color="primary" disabled={get(isSavingItems, 'id')} type="submit">
          <DoneOutline />
          {get(isSavingItems, 'id') && (
            <CircularProgress
              color="primary"
              size={34}
              style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                marginTop: -17,
                marginLeft: -17
              }}
            />
          )}
        </IconButton>
      </div>
    </form>
  );
};

export { RenameForm };
